import React, { useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { IntlProvider, useIntl } from 'react-intl';
import RecruitmentHub from '../../components/Hiring/RecruitmentHub';
import ApolloWrapper from '../StudentPlatform/ApolloWrapper';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';

const GET_RECRUITMENT_HUB = gql`
  query GetRecruitmentHub(
    $school: String
    $degree_program: String
    $graduation_year: Int
    $employment_type: String
    $candidate_track: String
    $state: String
    $includeCoaching: Boolean!
  ) {
    currentUser {
      id
      name
      email
    }
    myHiringPosts {
      id
      title
      position_type
      location
      remote
      description
      status
      views_count
      conversations_count
      saved_count
      published_at
      closed_at
      created_at
      updated_at
    }
    studentProspects(
      school: $school
      degree_program: $degree_program
      graduation_year: $graduation_year
      employment_type: $employment_type
      candidate_track: $candidate_track
      state: $state
    ) {
      id
      candidate_track
      age
      phone
      city
      state
      school
      degree_program
      graduation_year
      employment_type
      experience_level
      desired_location
      candidate_interests
      family_in_business
      resume_url
      video_url
      avatar_url
      why_interested
      certifications
      linkedin_url
      profile_completion_percentage
      user {
        id
        name
        email
        allowed_name
        profile_picture
        user_name
      }
    }
    getPersonalConversations {
      id
      participant1 { id }
      participant2 { id }
    }
    countryForCompany
    hiringSprintPrograms: sprintPrograms(goal: "hire") @include(if: $includeCoaching) {
      id
      title
      slug
      summary
      benefits
      duration_days
      level
      is_group
      hero_image_url
      goals
      avg_rating
      enrollment_count
      coachProfile {
        id
        name
        headline
        avatar_url
        credentials
        rating
        review_count
        verified
        org_name
      }
    }
  }
`;

const RecruitmentHubInner = ({ marketplaceSprintsEnabled }) => {
  const intl = useIntl();
  const [filters, setFilters] = React.useState({
    school: null,
    degree_program: null,
    graduation_year: null,
    employment_type: null,
    candidate_track: null,
    state: null,
  });

  const { loading, error, data, refetch } = useQuery(GET_RECRUITMENT_HUB, {
    variables: { ...filters, includeCoaching: !!marketplaceSprintsEnabled },
  });

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    refetch(newFilters);
  };

  const transformedPosts = useMemo(() => {
    return (data?.myHiringPosts || []).map(post => ({
      id: post.id,
      title: post.title,
      positionType: post.position_type,
      location: post.location,
      remote: post.remote,
      description: post.description,
      status: post.status,
      viewsCount: post.views_count,
      conversationsCount: post.conversations_count,
      savedCount: post.saved_count,
      publishedAt: post.published_at,
      closedAt: post.closed_at,
      createdAt: post.created_at,
      updatedAt: post.updated_at,
    }));
  }, [data?.myHiringPosts]);

  const messagingWithStudentIds = useMemo(() => {
    const conversations = data?.getPersonalConversations || [];
    const currentUserId = data?.currentUser?.id;
    const ids = new Set();

    if (currentUserId) {
      conversations.forEach(conv => {
        if (conv?.participant1 && conv?.participant2) {
          const otherParticipantId =
            conv.participant1.id === currentUserId
              ? conv.participant2.id
              : conv.participant1.id;
          ids.add(otherParticipantId);
        }
      });
    }

    return ids;
  }, [data]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-succession-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-succession-primary" />
          <p className="mt-4 text-succession-text-secondary">{intl.formatMessage({ id: 'hiring.container.hub.loading' })}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-succession-bg-primary flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{intl.formatMessage({ id: 'hiring.container.hub.error_heading' })}</p>
          <p className="text-succession-text-secondary mt-2">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-succession-primary text-white rounded-md hover:bg-succession-primary-dark"
          >
            {intl.formatMessage({ id: 'hiring.container.try_again' })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <RecruitmentHub
      currentUser={data?.currentUser}
      posts={transformedPosts}
      students={data?.studentProspects || []}
      messagingWithStudentIds={messagingWithStudentIds}
      onFilterChange={handleFilterChange}
      filters={filters}
      companyCountry={data?.countryForCompany}
      coachingPrograms={data?.hiringSprintPrograms || []}
      marketplaceSprintsEnabled={marketplaceSprintsEnabled}
    />
  );
};

const RecruitmentHubContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = railsContext && railsContext.currency;
  const locale = window.locale;
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
        <ApolloWrapper>
          <RecruitmentHubInner marketplaceSprintsEnabled={props.marketplaceSprintsEnabled} />
        </ApolloWrapper>
      </IntlProvider>
    </CurrencyProvider>
  );
};

export default RecruitmentHubContainer;
